import { useState, useEffect, useRef, useCallback } from 'react';
import { OtpModal } from '@/components/ui/otp-modal';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import { Link, useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../../services/api';

export interface OnboardingData {
  // User Identity
  fullName: string;
  email: string;
  username: string;

  // Business Info
  brandName: string;
  websiteUrl: string;
  businessDescription: string;
  gscClientEmail: string;
  gscConnected: boolean;
  gscValidationStatus: 'pending' | 'validating' | 'success' | 'error';
  gscErrorMessage: string;
  ga4Connected?: boolean;
  ga4PropertyId?: string;
  ga4PropertyName?: string;
  oauthAccessToken?: string;
  oauthAuthenticated?: boolean;
  hasGa4?: 'yes' | 'no' | null;
  hasGsc?: 'yes' | 'no' | null;
  locationScope: string;
  selectedLocations: string[];
  customerDescription: string;
  searchIntent: string | string[];
  offerings: string[];
  products: Array<{ name: string; url: string }>;
  services: Array<{ name: string; url: string }>;
  differentiators: string[];
  seoGoals: string[];
  selectedKeywords: string[];
  customKeywords: string[];
  competitors: Array<{ url: string; name: string }>;
  contentGraphicStyle: string;
  contentVoiceTone: string;
  primaryColor: string;
  secondaryColor: string;
  blogAuthors: Array<{ name: string; profileUrl: string; role?: string; bio?: string }>;
  topicAuthors?: Array<{ topic: string; authorName: string }>;
  contentLogoUrl: string;
  typography: string;
  pageLocations: string[];
  reportingChannels: string[];
  emails: string[];
  phones: string[];
  reportFrequency: string;
  selectedPlan?: string;
  accessToken: string;
  dashboardEmail: string;
  primaryFont?: string;
  secondaryFont?: string;
  homePageUrl?: string;
  productPageUrl?: string;
  contactPageUrl?: string;
  aboutPageUrl?: string;
  blogPageUrl?: string;

  // New fields for updated onboarding
  couponCode?: string;
  paymentOption?: 'credit' | 'paypal' | null;

  // Agency management (brand-side)
  isManagedByAgency?: boolean;
  agencyName?: string;
  agencyContactName?: string;
  agencyEmail?: string;
  agencyPhone?: string;
  agencyWebsite?: string;
  agencyAccessLevel?: 'view' | 'edit' | 'full' | null;

  // Channel Access (Step 3 — OAuth connections)
  youtubeConnected?: boolean;
  instagramConnected?: boolean;
  facebookConnected?: boolean;
  linkedinConnected?: boolean;
}

export default function Onboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnToAddClients = (location.state as { returnToAddClients?: boolean })?.returnToAddClients;

  const [selectedType, setSelectedType] = useState<'brand' | 'agency' | null>(null);
  const [step, setStep] = useState<'select' | 'agency-form' | 'success' | 'add-clients'>(() => {
    const isAgencySelected = sessionStorage.getItem('agency_selected_for_onboarding') === 'true';
    if (isAgencySelected) {
      const val = localStorage.getItem('agency_id');
      if (val && val !== 'undefined' && val !== 'null') return 'add-clients';
      return (localStorage.getItem('agency_onboarding_step') as 'select' | 'agency-form' | 'success' | 'add-clients') || 'select';
    }
    return 'select';
  });

  // Dev mode - allows skipping validation and jumping between steps
  const isDev = import.meta.env.DEV;

  // Agency form state
  const [agencyFormName, setAgencyFormName] = useState(localStorage.getItem('agency_onboarding_form_name') || '');
  const [agencyContactPerson, setAgencyContactPerson] = useState(localStorage.getItem('agency_onboarding_contact_person') || '');
  const [agencyFormEmail, setAgencyFormEmail] = useState(localStorage.getItem('agency_onboarding_email') || '');
  const [agencyFormPhone, setAgencyFormPhone] = useState(localStorage.getItem('agency_onboarding_phone') || '');
  const [agencyCredentials, setAgencyCredentials] = useState(localStorage.getItem('agency_onboarding_credentials') || '');
  const [focusAreas, setFocusAreas] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('agency_onboarding_focus_areas') || '[]'); } catch { return []; }
  });

  // Email OTP state (agency registration)
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailOtpError, setEmailOtpError] = useState('');
  const [emailOtpLoading, setEmailOtpLoading] = useState(false);
  const [emailOtpCountdown, setEmailOtpCountdown] = useState(0);
  const emailCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startEmailCountdown = (seconds = 60) => {
    if (emailCountdownRef.current) clearInterval(emailCountdownRef.current);
    setEmailOtpCountdown(seconds);
    emailCountdownRef.current = setInterval(() => {
      setEmailOtpCountdown(prev => {
        if (prev <= 1) {
          if (emailCountdownRef.current) clearInterval(emailCountdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendEmailOtp = async () => {
    if (!agencyFormEmail || !agencyFormEmail.includes('@')) {
      setEmailOtpError('Please enter a valid email address.');
      return;
    }
    setEmailOtpError('');
    setEmailOtpLoading(true);
    try {
      // Check if email already exists as a registered agency
      const agenciesRes = await apiService.getAvailableAgencies();
      if (agenciesRes.status === 'success' && agenciesRes.data?.agencies) {
        const emailNormalized = agencyFormEmail.trim().toLowerCase();
        const exists = agenciesRes.data.agencies.some(
          (ag: any) => ag.email?.trim().toLowerCase() === emailNormalized
        );
        if (exists) {
          setEmailOtpError('An agency with this email address is already registered. Please use a different email or log in.');
          setEmailOtpLoading(false);
          return;
        }
      }

      await apiService.sendOtp({ type: 'Email', value: agencyFormEmail });
      setEmailOtpSent(true);
      setEmailOtpCode('');
      startEmailCountdown(60);
    } catch (err: any) {
      setEmailOtpError(err?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setEmailOtpLoading(false);
    }
  };

  const handleVerifyEmailOtp = async () => {
    if (emailOtpCode.length < 6) {
      setEmailOtpError('Please enter the 6-digit code.');
      return;
    }
    setEmailOtpError('');
    setEmailOtpLoading(true);
    try {
      const res: any = await apiService.verifyOtp({ type: 'Email', otp: emailOtpCode, value: agencyFormEmail });
      if (res?.message === 'OTP verified successfully' || res?.status === 'success') {
        setEmailVerified(true);
        setEmailOtpError('');
      } else {
        setEmailOtpError(res?.message || 'Invalid or expired code. Please try again.');
      }
    } catch (err: any) {
      setEmailOtpError(err?.message || 'Invalid OTP. Please try again.');
    } finally {
      setEmailOtpLoading(false);
    }
  };

  const handleResendEmailOtp = async () => {
    if (emailOtpCountdown > 0) return;
    await handleSendEmailOtp();
  };

  // Phone OTP modal state (agency registration)
  const [phoneOtpModal, setPhoneOtpModal] = useState<{
    open: boolean;
    phone: string;
    otpCode: string;
    loading: boolean;
    error: string;
    verified: boolean;
  } | null>(null);
  const [phoneVerified, setPhoneVerified] = useState(false);

  const handleOpenPhoneOtp = async () => {
    const phone = agencyFormPhone.trim();
    if (!phone) {
      setSubmitError('Please enter a phone number before verifying.');
      return;
    }
    
    try {
      await apiService.sendOtp({ type: 'Phone', value: phone });
      setPhoneOtpModal({ open: true, phone, otpCode: '', loading: false, error: '', verified: false });
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to send OTP.';
      setSubmitError(detail);
      setPhoneOtpModal(null);
    }
  };

  const handleVerifyPhoneOtp = async () => {
    if (!phoneOtpModal) return;
    if (phoneOtpModal.otpCode.length < 6) {
      setPhoneOtpModal(prev => prev ? { ...prev, error: 'Please enter the 6-digit code.' } : prev);
      return;
    }
    setPhoneOtpModal(prev => prev ? { ...prev, loading: true, error: '' } : prev);
    try {
      const res: any = await apiService.verifyOtp({ type: 'Phone', otp: phoneOtpModal.otpCode, value: phoneOtpModal.phone });
      if (res?.status === 'success' || res?.message?.toLowerCase().includes('verified')) {
        setPhoneOtpModal(prev => prev ? { ...prev, loading: false, verified: true } : prev);
        setPhoneVerified(true);
        setTimeout(() => setPhoneOtpModal(null), 2000);
      } else {
        setPhoneOtpModal(prev => prev ? { ...prev, loading: false, error: res?.message || 'Invalid or expired code.' } : prev);
      }
    } catch (err: any) {
      setPhoneOtpModal(prev => prev ? { ...prev, loading: false, error: err?.message || 'Verification failed.' } : prev);
    }
  };

  // Add clients state
  const [clientBrands, setClientBrands] = useState<string[]>(['']);

  // Pending brands (added but not yet onboarded)
  interface PendingBrand { id: number; brand_name: string; status: string; }
  const [pendingBrands, setPendingBrands] = useState<PendingBrand[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);

  // Brand access request state
  type LookupResult = {
    found: boolean;
    brand_user_id?: string;
    business_name?: string;
  };
  const [lookupResults, setLookupResults] = useState<Record<number, LookupResult>>({});
  const lookupTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const [registeredAgencyId, setRegisteredAgencyId] = useState<string | null>(() => {
    const val = localStorage.getItem('agency_id');
    return val && val !== 'undefined' && val !== 'null' ? val : null;
  });

  // Fetch pending brands when entering add-clients step
  useEffect(() => {
    if (step === 'add-clients' && registeredAgencyId) {
      setPendingLoading(true);
      apiService.getAgencyClients(registeredAgencyId)
        .then(res => {
          if (res.status === 'success' && res.data) {
            setPendingBrands(
              (res.data.clients as PendingBrand[]).filter((c: PendingBrand) => c.status !== 'active')
            );
          }
        })
        .catch(() => { })
        .finally(() => setPendingLoading(false));
    }
  }, [step, registeredAgencyId]);

  // Modal state
  const [accessModal, setAccessModal] = useState<{
    open: boolean;
    index: number;
    brand_user_id: string;
    business_name: string;
    phase: 'confirm' | 'otp';
    otpValue: string;
    otpError: string;
    otpSuccess: boolean;
    timeLeft: number; // seconds
    resendCooldown: number;
  } | null>(null);

  const otpTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const resendTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearOtpTimers = () => {
    if (otpTimerRef.current) clearInterval(otpTimerRef.current);
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
  };

  useEffect(() => {
    return () => clearOtpTimers();
  }, []);

  const startOtpCountdown = (modal: NonNullable<typeof accessModal>) => {
    clearOtpTimers();
    setAccessModal(prev => prev ? { ...prev, timeLeft: 180, resendCooldown: 60 } : prev);
    otpTimerRef.current = setInterval(() => {
      setAccessModal(prev => {
        if (!prev) return prev;
        const newTime = prev.timeLeft - 1;
        return { ...prev, timeLeft: Math.max(0, newTime) };
      });
    }, 1000);
    resendTimerRef.current = setInterval(() => {
      setAccessModal(prev => {
        if (!prev) return prev;
        const newCd = prev.resendCooldown - 1;
        if (newCd <= 0 && resendTimerRef.current) clearInterval(resendTimerRef.current);
        return { ...prev, resendCooldown: Math.max(0, newCd) };
      });
    }, 1000);
  };

  const fmtTime = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const handleBrandInputChange = useCallback((index: number, value: string) => {
    setClientBrands(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
    // Clear previous lookup for this index
    setLookupResults(prev => { const n = { ...prev }; delete n[index]; return n; });

    // Optimistically update the pending brands list
    // Negative IDs (-(index+1)) are used for optimistic/unsaved entries
    const optimisticId = -(index + 1);
    if (!value.trim()) {
      // Remove optimistic entry if field is cleared
      setPendingBrands(prev => prev.filter(b => b.id !== optimisticId));
    } else {
      // Upsert optimistic entry
      setPendingBrands(prev => {
        const exists = prev.find(b => b.id === optimisticId);
        if (exists) {
          return prev.map(b => b.id === optimisticId ? { ...b, brand_name: value.trim() } : b);
        }
        return [...prev, { id: optimisticId, brand_name: value.trim(), status: 'pending' }];
      });
    }

    // Debounce lookup by 500ms
    if (lookupTimers.current[index]) clearTimeout(lookupTimers.current[index]);
    if (!value.trim()) return;
    lookupTimers.current[index] = setTimeout(async () => {
      try {
        const res: any = await apiService.lookupBrand(value.trim());
        if (res.data?.found) {
          setLookupResults(prev => ({ ...prev, [index]: res.data }));
        }
      } catch { /* silent */ }
    }, 500);
  }, []);

  const openAccessModal = (index: number) => {
    const result = lookupResults[index];
    if (!result) return;
    setAccessModal({
      open: true,
      index,
      brand_user_id: result.brand_user_id!,
      business_name: result.business_name!,
      phase: 'confirm',
      otpValue: '',
      otpError: '',
      otpSuccess: false,
      timeLeft: 180,
      resendCooldown: 60,
    });
  };

  const handleRequestAccess = async () => {
    if (!accessModal || !registeredAgencyId) return;
    setIsSubmitting(true);
    try {
      const res: any = await apiService.requestBrandAccess({
        agency_id: registeredAgencyId,
        brand_user_id: accessModal.brand_user_id,
      });
      if (res.status === 'success') {
        const updated = { ...accessModal, phase: 'otp' as const };
        setAccessModal(updated);
        startOtpCountdown(updated);
      } else {
        setAccessModal(prev => prev ? { ...prev, otpError: res.message || 'Failed to send OTP' } : prev);
      }
    } catch (err) {
      setAccessModal(prev => prev ? { ...prev, otpError: 'Failed to send access request' } : prev);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!accessModal || !registeredAgencyId) return;
    if (accessModal.timeLeft === 0) {
      setAccessModal(prev => prev ? { ...prev, otpError: 'Code expired. Please request a new one.' } : prev);
      return;
    }
    setIsSubmitting(true);
    try {
      const res: any = await apiService.verifyBrandAccess({
        agency_id: registeredAgencyId,
        brand_user_id: accessModal.brand_user_id,
        otp_code: accessModal.otpValue,
      });
      if (res.status === 'success') {
        clearOtpTimers();
        setAccessModal(prev => prev ? { ...prev, otpSuccess: true, otpError: '' } : prev);
        // Remove from client brand inputs (it's now linked)
        setClientBrands(prev => {
          const updated = [...prev];
          updated[accessModal.index] = '';
          return updated;
        });
        setLookupResults(prev => { const n = { ...prev }; delete n[accessModal.index]; return n; });
        setTimeout(() => setAccessModal(null), 2500);
      } else {
        setAccessModal(prev => prev ? { ...prev, otpError: res.message || 'Invalid or expired code. Try again.' } : prev);
      }
    } catch (err) {
      setAccessModal(prev => prev ? { ...prev, otpError: 'Verification failed. Please try again.' } : prev);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (!accessModal || !registeredAgencyId || accessModal.resendCooldown > 0) return;
    await handleRequestAccess();
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');


  useEffect(() => {
    if (returnToAddClients) {
      setStep('add-clients');
    }
  }, [returnToAddClients]);

  useEffect(() => {
    localStorage.setItem('agency_onboarding_form_name', agencyFormName);
    localStorage.setItem('agency_onboarding_contact_person', agencyContactPerson);
    localStorage.setItem('agency_onboarding_email', agencyFormEmail);
    localStorage.setItem('agency_onboarding_phone', agencyFormPhone);
    localStorage.setItem('agency_onboarding_credentials', agencyCredentials);
    localStorage.setItem('agency_onboarding_focus_areas', JSON.stringify(focusAreas));
    localStorage.setItem('agency_onboarding_step', step);
  }, [agencyFormName, agencyContactPerson, agencyFormEmail, agencyFormPhone, agencyCredentials, focusAreas, step]);

  const toggleFocusArea = (area: string) => {
    setFocusAreas(prev =>
      prev.includes(area)
        ? prev.filter(a => a !== area)
        : [...prev, area]
    );
  };

  const handleAgencyClick = () => {
    setSelectedType('agency');
    sessionStorage.setItem('agency_selected_for_onboarding', 'true');
    const val = localStorage.getItem('agency_id');
    if (val && val !== 'undefined' && val !== 'null') {
      setStep('add-clients');
    } else {
      setStep('agency-form');
    }
  };

  const handleAgencySubmit = async () => {
    if (!emailVerified && !isDev) {
      setSubmitError('Please verify your email address before registering.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const response: any = await apiService.registerAgency({
        agency_name: agencyFormName,
        contact_person: agencyContactPerson,
        email: agencyFormEmail,
        phone: agencyFormPhone || undefined,
        credentials: agencyCredentials || undefined,
        focus_areas: focusAreas,
      });

      if (response.status === 'exists') {
        setEmailVerified(false);
        setEmailOtpSent(false);
        setEmailOtpCode('');
        setSubmitError('An agency with this email address is already registered. Please use a different email or log in.');
      } else if (response.status === 'success') {
        const agId = response.agency_id || response.user_id || response.data?.agency_id;
        if (agId) {
          setRegisteredAgencyId(agId);
          localStorage.setItem('agency_id', agId);
          localStorage.setItem('account_type', 'agency');
          localStorage.removeItem('agency_onboarding_form_name');
          localStorage.removeItem('agency_onboarding_contact_person');
          localStorage.removeItem('agency_onboarding_email');
          localStorage.removeItem('agency_onboarding_phone');
          localStorage.removeItem('agency_onboarding_credentials');
          localStorage.removeItem('agency_onboarding_focus_areas');
        }
        setStep('success');
      } else {
        setSubmitError(response.message || 'Registration failed');
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const focusAreaOptions = [
    { id: 'seo', label: 'SEO', icon: 'ri-search-line' },
    { id: 'content', label: 'Content Marketing', icon: 'ri-article-line' },
    { id: 'social', label: 'Social Media', icon: 'ri-share-line' },
    { id: 'paid', label: 'Paid Advertising', icon: 'ri-advertisement-line' },
    { id: 'email', label: 'Email Marketing', icon: 'ri-mail-line' },
    { id: 'analytics', label: 'Analytics', icon: 'ri-bar-chart-line' },
  ];

  // Account Type Selection Screen
  if (step === 'select') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50">
        <div className="max-w-4xl mx-auto px-4 py-16">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              Welcome to Marketing4Sight
            </h1>
            <p className="text-gray-600 text-lg">
              Choose how you want to get started
            </p>
          </div>

          {/* Cards */}
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Brand Card */}
            <div
              onClick={() => navigate('/brand-onboarding')}
              className="bg-white border-2 border-gray-200 rounded-2xl p-8 cursor-pointer hover:border-teal-400 hover:shadow-lg transition-all group"
            >
              <div className="w-16 h-16 rounded-2xl bg-teal-50 flex items-center justify-center mb-6 group-hover:bg-teal-100 transition-colors">
                <i className="ri-building-line text-teal-600 text-3xl"></i>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Onboard a Brand</h2>
              <p className="text-gray-600 text-sm mb-6">
                Perfect for individual brands looking to manage their own marketing operations
              </p>
              <div className="flex items-center text-teal-600 font-semibold text-sm group-hover:gap-2 transition-all">
                Get Started <i className="ri-arrow-right-line ml-1"></i>
              </div>
            </div>

            {/* Agency Card */}
            <div
              onClick={handleAgencyClick}
              className={`bg-white border-2 rounded-2xl p-8 cursor-pointer hover:shadow-lg transition-all group ${selectedType === 'agency' ? 'border-blue-400 shadow-lg' : 'border-gray-200 hover:border-blue-400'
                }`}
            >
              <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                <i className="ri-team-line text-blue-600 text-3xl"></i>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Register an Agency</h2>
              <p className="text-gray-600 text-sm mb-6">
                Ideal for agencies managing marketing for multiple brands and clients
              </p>
              <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:gap-2 transition-all">
                Register Now <i className="ri-arrow-right-line ml-1"></i>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-12">
            <p className="text-sm text-gray-500">
              Already have an account?{' '}
              <Link to="/?login=true" className="text-teal-600 hover:underline font-medium">
                Log In
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Agency Registration Form
  if (step === 'agency-form') {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
          <div className="max-w-2xl mx-auto px-4 py-12">
            {/* Dev Mode Banner */}
            {isDev && (
              <div className="mb-4 bg-amber-50 border border-amber-300 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-amber-700 flex items-center gap-1">
                    <i className="ri-code-line"></i> DEV MODE
                  </span>
                  <div className="flex gap-2">
                    {(['select', 'agency-form', 'success', 'add-clients'] as const).map(s => (
                      <button
                        key={s}
                        onClick={() => setStep(s)}
                        className={`px-2 py-1 text-xs rounded font-medium transition-colors ${step === s
                          ? 'bg-amber-600 text-white'
                          : 'bg-white text-amber-700 hover:bg-amber-100 border border-amber-300'
                          }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Back Button */}
            <button
              onClick={() => {
                sessionStorage.removeItem('agency_selected_for_onboarding');
                setStep('select');
              }}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-8 text-sm font-medium transition-colors"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Selection
            </button>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
              {/* Header */}
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <i className="ri-team-line text-blue-600 text-xl"></i>
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Register Your Agency</h1>
              </div>
              <p className="text-gray-600 text-sm mb-8 ml-13">
                Tell us about your marketing agency
              </p>

              {/* Form Fields */}
              <div className="space-y-5">
                {/* Agency Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Agency Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={agencyFormName}
                    onChange={(e) => setAgencyFormName(e.target.value)}
                    placeholder="e.g., Digital Growth Partners"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* Contact Person & Email */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Contact Person <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      value={agencyContactPerson}
                      onChange={(e) => setAgencyContactPerson(e.target.value)}
                      placeholder="Your full name"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="email"
                        value={agencyFormEmail}
                        onChange={(e) => {
                          setAgencyFormEmail(e.target.value);
                          setEmailVerified(false);
                          setEmailOtpSent(false);
                          setEmailOtpCode('');
                          setEmailOtpError('');
                        }}
                        disabled={emailVerified}
                        placeholder="agency@example.com"
                        className={`flex-1 px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all ${emailVerified
                            ? 'border-green-300 bg-green-50 text-green-700'
                            : 'border-gray-200 focus:ring-blue-500'
                          }`}
                      />
                      {!emailVerified && (
                        <button
                          type="button"
                          onClick={handleSendEmailOtp}
                          disabled={emailOtpLoading || (emailOtpSent && emailOtpCountdown > 0)}
                          className="px-3 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white rounded-lg text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1"
                        >
                          {emailOtpLoading ? (
                            <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Sending...</>
                          ) : emailOtpSent ? (
                            <><i className="ri-mail-send-line"></i>{emailOtpCountdown > 0 ? ` ${emailOtpCountdown}s` : ' Resend'}</>
                          ) : (
                            <><i className="ri-mail-send-line"></i> Send OTP</>
                          )}
                        </button>
                      )}
                      {emailVerified && (
                        <div className="px-3 py-2.5 bg-green-100 border border-green-200 rounded-lg text-xs text-green-700 font-medium flex items-center gap-1 whitespace-nowrap">
                          <i className="ri-shield-check-line"></i> Verified
                        </div>
                      )}
                    </div>

                    {emailOtpError && !emailOtpSent && (
                      <div className="mt-2 flex items-center gap-1.5 bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-xs text-red-600 animate-fadeIn">
                        <i className="ri-error-warning-line text-red-500 shrink-0"></i>
                        <span>{emailOtpError}</span>
                      </div>
                    )}

                    {/* OTP Verification Box */}
                    {emailOtpSent && !emailVerified && (
                      <div className="mt-2 bg-white border border-blue-200 rounded-xl shadow-sm overflow-hidden">
                        <div className="bg-blue-50/80 px-3 py-2 border-b border-blue-100 flex items-center gap-2">
                          <div className="w-6 h-6 rounded-md bg-blue-500 flex items-center justify-center shrink-0">
                            <i className="ri-mail-check-line text-white text-xs"></i>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-900">Email Verification</p>
                            <p className="text-xs text-gray-500">Code sent to <span className="font-medium text-gray-700">{agencyFormEmail}</span></p>
                          </div>
                        </div>
                        <div className="p-3">
                          <div className="flex items-center gap-2">
                            <Input
                              type="text"
                              inputMode="numeric"
                              autoComplete="one-time-code"
                              value={emailOtpCode}
                              onChange={(e) => setEmailOtpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                              placeholder="● ● ● ● ● ●"
                              maxLength={6}
                              className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-0 text-center tracking-[0.5em] font-mono placeholder:text-gray-300 placeholder:tracking-normal"
                            />
                            <button
                              type="button"
                              onClick={handleVerifyEmailOtp}
                              disabled={emailOtpCode.length < 6 || emailOtpLoading}
                              className="cursor-pointer px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-400 text-white rounded-lg text-xs font-semibold transition-colors whitespace-nowrap"
                            >
                              {emailOtpLoading ? 'Verifying...' : 'Verify'}
                            </button>
                          </div>
                          {emailOtpError && (
                            <div className="mt-2 flex items-center gap-1.5 bg-red-50 border border-red-100 rounded-lg px-2 py-1.5">
                              <i className="ri-error-warning-line text-red-500 text-xs"></i>
                              <p className="text-xs text-red-600">{emailOtpError}</p>
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-2">
                            {emailOtpCountdown > 0 ? (
                              <p className="text-xs text-gray-500"><i className="ri-time-line"></i> Resend in <span className="font-medium text-blue-600">{emailOtpCountdown}s</span></p>
                            ) : (
                              <button type="button" onClick={handleResendEmailOtp} className="cursor-pointer text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                                <i className="ri-refresh-line"></i> Resend OTP
                              </button>
                            )}
                            <span className="text-xs text-gray-400">Code valid for 10 min</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Phone Number (With country code)
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="tel"
                      value={agencyFormPhone}
                      onChange={(e) => {
                        setAgencyFormPhone(e.target.value);
                        setPhoneVerified(false);
                      }}
                      disabled={phoneVerified}
                      placeholder="+1 (555) 000-0000"
                      className={`flex-1 px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all ${phoneVerified
                          ? 'border-green-300 bg-green-50 text-green-700'
                          : 'border-gray-200 focus:ring-blue-500'
                        }`}
                    />
                    {agencyFormPhone.trim() && !phoneVerified && (
                      <button
                        type="button"
                        onClick={handleOpenPhoneOtp}
                        className="cursor-pointer px-3 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1"
                      >
                        <i className="ri-phone-line"></i> Verify
                      </button>
                    )}
                    {phoneVerified && (
                      <div className="px-3 py-2.5 bg-green-100 border border-green-200 rounded-lg text-xs text-green-700 font-medium flex items-center gap-1 whitespace-nowrap">
                        <i className="ri-shield-check-line"></i> Verified
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Optional — verify your number via SMS OTP</p>
                </div>

                {/* Focus Areas */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Agency Focus Areas
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {focusAreaOptions.map(area => (
                      <button
                        key={area.id}
                        type="button"
                        onClick={() => toggleFocusArea(area.id)}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${focusAreas.includes(area.id)
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                      >
                        <i className={`${area.icon} text-base`}></i>
                        {area.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Agency Credentials / Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Agency Credentials / Description
                  </label>
                  <Textarea
                    value={agencyCredentials}
                    onChange={(e) => setAgencyCredentials(e.target.value)}
                    placeholder="Brief description of your agency's experience and certifications..."
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
                  />
                </div>
              </div>

              {/* Error */}
              {submitError && (
                <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {submitError}
                </div>
              )}

              {/* Submit */}
              <button
                onClick={handleAgencySubmit}
                disabled={isSubmitting || (!isDev && (!agencyFormName || !agencyContactPerson || !agencyFormEmail || !emailVerified))}
                className="cursor-pointer w-full mt-8 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold text-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <><i className="ri-loader-4-line animate-spin mr-1"></i> Registering...</>
                ) : isDev && (!agencyFormName || !agencyContactPerson || !agencyFormEmail) ? (
                  <><i className="ri-code-line mr-1"></i> Skip & Register (Dev)</>
                ) : !emailVerified && !isDev ? (
                  <><i className="ri-mail-lock-line mr-1"></i> Verify Email to Register</>
                ) : (
                  <>Register Agency <i className="ri-arrow-right-line"></i></>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Phone OTP Modal */}
        {phoneOtpModal?.open && (
          <OtpModal
            isOpen={phoneOtpModal.open}
            title="Verify Phone Number"
            description={`A 6-digit code was sent to your mobile number: ${phoneOtpModal.phone}`}
            onClose={() => setPhoneOtpModal(null)}
            onVerify={async (otp) => {
              // Map to the existing logic
              const res = await apiService.verifyOtp({ type: 'Phone', value: phoneOtpModal.phone, otp });
              if (res?.status === 'success' || res?.message?.toLowerCase().includes('verified')) {
                  setPhoneVerified(true);
                  setPhoneOtpModal(null);
              } else {
                  throw new Error(res?.message || 'Invalid or expired code.');
              }
            }}
            resendOtp={async () => {
              await apiService.sendOtp({ type: 'Phone', value: phoneOtpModal.phone });
            }}
          />
        )}
      </>
    );
  }

  // Agency Registration Success
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white flex items-center justify-center px-6">
        <div className="max-w-md w-full">
          {/* Dev Mode Banner */}
          {isDev && (
            <div className="mb-4 bg-amber-50 border border-amber-300 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-amber-700 flex items-center gap-1">
                  <i className="ri-code-line"></i> DEV MODE
                </span>
                <div className="flex gap-2">
                  {(['select', 'agency-form', 'success', 'add-clients'] as const).map(s => (
                    <button
                      key={s}
                      onClick={() => setStep(s)}
                      className={`px-2 py-1 text-xs rounded font-medium transition-colors ${step === s
                        ? 'bg-amber-600 text-white'
                        : 'bg-white text-amber-700 hover:bg-amber-100 border border-amber-300'
                        }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-lg p-8">
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-green-500 text-4xl"></i>
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Agency Registered Successfully! 🎉
              </h1>
              <p className="text-sm text-gray-500">
                Your agency has been set up. You can now add client brands.
              </p>
            </div>

            {/* Next Steps */}
            <div className="bg-gray-50 rounded-xl p-5 mb-6">
              <h3 className="text-sm font-bold text-gray-900 text-center mb-4">
                What's Next?
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-add-circle-line text-teal-500 text-lg mt-0.5"></i>
                  <p className="text-sm text-gray-700">
                    <strong>Add your client brands</strong> to manage their SEO from your dashboard
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-dashboard-line text-teal-500 text-lg mt-0.5"></i>
                  <p className="text-sm text-gray-700">
                    Access your <strong>Agency Dashboard</strong> to track all brands
                  </p>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mb-6">
              <button
                onClick={() => setStep('add-clients')}
                className="flex-1 px-6 py-3 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 transition-colors flex items-center justify-center gap-2"
              >
                Add Clients / Brands
                <i className="ri-arrow-right-line"></i>
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem('agency_onboarding_step');
                  sessionStorage.removeItem('agency_selected_for_onboarding');
                  navigate('/agency-dashboard');
                }}
                className="flex-1 px-6 py-3 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>

            {/* Help */}
            <p className="text-center text-xs text-gray-500">
              Need Help? Contact us at <a href="mailto:4Sight.platform@gmail.com" className="cursor-pointer text-teal-600 hover:underline">4Sight.platform@gmail.com</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Add Clients / Brands Screen
  if (step === 'add-clients') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-2xl mx-auto px-4 py-12">
          {/* Dev Mode Banner */}
          {isDev && (
            <div className="mb-4 bg-amber-50 border border-amber-300 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-amber-700 flex items-center gap-1">
                  <i className="ri-code-line"></i> DEV MODE
                </span>
                <div className="flex gap-2">
                  {(['select', 'agency-form', 'success', 'add-clients'] as const).map(s => (
                    <button
                      key={s}
                      onClick={() => setStep(s)}
                      className={`px-2 py-1 text-xs rounded font-medium transition-colors ${step === s
                        ? 'bg-amber-600 text-white'
                        : 'bg-white text-amber-700 hover:bg-amber-100 border border-amber-300'
                        }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Back Button */}
          <button
            onClick={() => setStep('success')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-8 text-sm font-medium transition-colors"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back
          </button>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
                <i className="ri-building-line text-teal-600 text-xl"></i>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Add Client Brands</h1>
            </div>
            <p className="text-gray-600 text-sm mb-8">
              Add the brands you want to manage. If a brand is already on Marketing4Sight, you will be prompted to request access.
            </p>

            {/* Single Brand Input */}
            <div className="mb-6">
              <div className="flex items-center gap-3">
                <Input
                  type="text"
                  value={clientBrands[0] || ''}
                  onChange={(e) => handleBrandInputChange(0, e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const val = (clientBrands[0] || '').trim();
                      if (!val || lookupResults[0]?.found) return;
                      if (!registeredAgencyId) {
                        setSubmitError('Agency session not found. Please complete agency registration first.');
                        return;
                      }
                      setIsSubmitting(true);
                      setSubmitError('');
                      try {
                        await apiService.addAgencyClients({ agency_id: registeredAgencyId, brand_names: [val] });
                        setClientBrands(['']);
                        setLookupResults({});
                        const res = await apiService.getAgencyClients(registeredAgencyId);
                        if (res.status === 'success' && res.data) {
                          setPendingBrands((res.data.clients as PendingBrand[]).filter((c: PendingBrand) => c.status !== 'active'));
                        }
                      } catch (err: any) {
                        setSubmitError(err?.message || 'Failed to add brand. Please try again.');
                      } finally {
                        setIsSubmitting(false);
                      }
                    }
                  }}
                  placeholder="Brand name (exact match)"
                  className={`flex-1 px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all ${lookupResults[0]?.found
                    ? 'border-amber-400 ring-2 ring-amber-200 bg-amber-50'
                    : 'border-gray-200 focus:ring-blue-500'
                    }`}
                />
                <button
                  type="button"
                  onClick={async () => {
                    const val = (clientBrands[0] || '').trim();
                    if (!val || lookupResults[0]?.found) return;
                    if (!registeredAgencyId) {
                      setSubmitError('Agency session not found. Please complete agency registration first.');
                      return;
                    }
                    setIsSubmitting(true);
                    setSubmitError('');
                    try {
                      await apiService.addAgencyClients({ agency_id: registeredAgencyId, brand_names: [val] });
                      setClientBrands(['']);
                      setLookupResults({});
                      const res = await apiService.getAgencyClients(registeredAgencyId);
                      if (res.status === 'success' && res.data) {
                        setPendingBrands((res.data.clients as PendingBrand[]).filter((c: PendingBrand) => c.status !== 'active'));
                      }
                    } catch (err: any) {
                      setSubmitError(err?.message || 'Failed to add brand. Please try again.');
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                  disabled={isSubmitting || !(clientBrands[0] || '').trim() || !!lookupResults[0]?.found}
                  className="px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                >
                  {isSubmitting ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>} Add Brand
                </button>
              </div>

              {/* Match banner */}
              {lookupResults[0]?.found && (
                <div className="mt-2 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <i className="ri-information-line text-amber-500"></i>
                  <span className="text-xs text-amber-800 font-medium flex-1">
                    <strong>{lookupResults[0].business_name}</strong> is already on Marketing4Sight.
                  </span>
                  <button
                    onClick={() => openAccessModal(0)}
                    className="text-xs font-semibold text-blue-600 hover:text-blue-800 whitespace-nowrap transition-colors"
                  >
                    Request Access &rarr;
                  </button>
                </div>
              )}
            </div>

            {/* Error */}
            {submitError && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {submitError}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 mt-2">
              <button
                onClick={async () => {
                  // Collect all unsaved optimistic brands (id < 0)
                  const unsavedBrands = pendingBrands
                    .filter(b => b.id < 0)
                    .map(b => b.brand_name);
                  if (unsavedBrands.length > 0 && registeredAgencyId) {
                    setIsSubmitting(true);
                    setSubmitError('');
                    try {
                      await apiService.addAgencyClients({
                        agency_id: registeredAgencyId,
                        brand_names: unsavedBrands,
                      });
                      // Remove optimistic entries, re-fetch real ones
                      setPendingBrands(prev => prev.filter(b => b.id > 0));
                      apiService.getAgencyClients(registeredAgencyId)
                        .then(res => {
                          if (res.status === 'success' && res.data) {
                            setPendingBrands(
                              (res.data.clients as PendingBrand[]).filter((c: PendingBrand) => c.status !== 'active')
                            );
                          }
                        })
                        .catch(() => { });
                    } catch (err) {
                      setSubmitError(err instanceof Error ? err.message : 'Failed to save brands');
                      setIsSubmitting(false);
                      return;
                    }
                    setIsSubmitting(false);
                  }
                  localStorage.removeItem('agency_onboarding_step');
                  sessionStorage.removeItem('agency_selected_for_onboarding');
                  navigate('/agency-dashboard');
                }}
                disabled={isSubmitting}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <><i className="ri-loader-4-line animate-spin mr-1"></i> Saving...</>
                ) : (
                  <>Save & Go to Dashboard <i className="ri-arrow-right-line"></i></>
                )}
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem('agency_onboarding_step');
                  sessionStorage.removeItem('agency_selected_for_onboarding');
                  navigate('/agency-dashboard');
                }}
                className="px-6 py-3 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Skip for Now
              </button>
            </div>
          </div>

          {/* Pending Brands Section */}
          {pendingBrands.length > 0 && (
            <div className="mt-8 bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                  <i className="ri-time-line text-amber-500 text-xl"></i>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Pending Brands</h2>
                  <p className="text-sm text-gray-500">Brands awaiting setup or access verification</p>
                </div>
              </div>

              <div className="space-y-4">
                {pendingBrands.map((brand) => {
                  const isFailed = brand.status === 'access_failed' || brand.status === 'failed';
                  const isOptimistic = brand.id < 0;
                  return (
                    <div key={brand.id} className="flex items-center justify-between p-5 rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100/50 transition-colors">
                      <div>
                        <h3 className="font-semibold text-gray-900 text-base mb-1">{brand.brand_name}</h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isFailed ? 'bg-red-50 text-red-600' : isOptimistic ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'}`}>
                          {isFailed ? (
                            <><i className="ri-error-warning-fill mr-1"></i> Access Failed</>
                          ) : isOptimistic ? (
                            <><i className="ri-pencil-line mr-1"></i> Not saved yet</>
                          ) : (
                            <><i className="ri-loader-4-line animate-spin mr-1"></i> Setup Required</>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {!isOptimistic && (
                          <button
                            onClick={() => navigate('/brand-onboarding', {
                              state: {
                                agency_id: registeredAgencyId,
                                brand_name: brand.brand_name,
                                isManagedByAgency: true,
                              }
                            })}
                            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${isFailed ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-teal-500 text-white hover:bg-teal-600'}`}
                          >
                            {isFailed ? (
                              <>Retry Access <i className="ri-refresh-line"></i></>
                            ) : (
                              <>Start Onboarding <i className="ri-arrow-right-line"></i></>
                            )}
                          </button>
                        )}
                        <button
                          onClick={async () => {
                            if (isOptimistic) {
                              // Just remove from local state for unsaved entries
                              setPendingBrands(prev => prev.filter(b => b.id !== brand.id));
                            } else {
                              // Call API to delete the real record
                              try {
                                await apiService.removeAgencyClient(registeredAgencyId!, brand.id);
                                setPendingBrands(prev => prev.filter(b => b.id !== brand.id));
                              } catch { /* silent */ }
                            }
                          }}
                          className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          title="Remove brand"
                        >
                          <i className="ri-delete-bin-line text-lg"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>

        {/* ==== Brand Access Modal ==== */}
        {
          accessModal?.open && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              {/* Backdrop */}
              <div
                className="cursor-pointer absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={() => { clearOtpTimers(); setAccessModal(null); }}
              />

              {/* Panel */}
              <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                      <i className="ri-shield-keyhole-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h2 className="text-white font-bold text-base">Brand Access Request</h2>
                      <p className="text-blue-100 text-xs">{accessModal.business_name}</p>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  {accessModal.phase === 'confirm' && (
                    <>
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
                        <div className="flex gap-3">
                          <i className="ri-information-line text-amber-500 text-xl flex-shrink-0 mt-0.5"></i>
                          <p className="text-sm text-amber-900 leading-relaxed">
                            <strong>{accessModal.business_name}</strong> is already on Marketing4Sight.
                            Clicking <strong>Request Access</strong> will send a one-time verification code
                            to the brand registered email. Ask them to share it with you.
                          </p>
                        </div>
                      </div>

                      {accessModal.otpError && (
                        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-lg text-sm">
                          {accessModal.otpError}
                        </div>
                      )}

                      <div className="flex gap-3">
                        <button
                          onClick={handleRequestAccess}
                          disabled={isSubmitting}
                          className="cursor-pointer flex-1 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          {isSubmitting
                            ? <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                            : <><i className="ri-mail-send-line"></i> Request Access</>
                          }
                        </button>
                        <button
                          onClick={() => { clearOtpTimers(); setAccessModal(null); }}
                          className="px-5 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  )}

                  {accessModal.phase === 'otp' && (
                    <>
                      {accessModal.otpSuccess ? (
                        <div className="text-center py-6">
                          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                            <i className="ri-checkbox-circle-line text-green-500 text-4xl"></i>
                          </div>
                          <h3 className="font-bold text-gray-900 mb-1">Access Granted!</h3>
                          <p className="text-sm text-gray-500">
                            <strong>{accessModal.business_name}</strong> has been linked to your agency.
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="text-center mb-5">
                            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${accessModal.timeLeft > 60
                              ? 'bg-green-50 text-green-700'
                              : accessModal.timeLeft > 20
                                ? 'bg-amber-50 text-amber-700'
                                : 'bg-red-50 text-red-700'
                              }`}>
                              <i className="ri-time-line"></i>
                              {accessModal.timeLeft > 0 ? fmtTime(accessModal.timeLeft) : 'Code Expired'}
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              A 6-digit code was sent to the brand registered email.
                              Ask the brand to share the code with you.
                            </p>
                          </div>

                          <div className="mb-4">
                            <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                              Enter 6-Digit Code
                            </label>
                            <Input
                              type="text"
                              maxLength={6}
                              value={accessModal.otpValue}
                              disabled={accessModal.timeLeft === 0}
                              onChange={e => setAccessModal(prev => prev ? {
                                ...prev,
                                otpValue: e.target.value.replace(/\D/g, '').slice(0, 6),
                                otpError: ''
                              } : prev)}
                              placeholder="______"
                              className={`w-full text-center text-3xl font-mono tracking-[0.5em] px-4 py-4 border-2 rounded-xl focus:outline-none transition-all ${accessModal.timeLeft === 0
                                ? 'bg-gray-50 border-gray-200 text-gray-300 cursor-not-allowed'
                                : accessModal.otpError
                                  ? 'border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100'
                                  : 'border-blue-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
                                }`}
                            />
                            {accessModal.otpError && (
                              <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                                <i className="ri-error-warning-line"></i>{accessModal.otpError}
                              </p>
                            )}
                          </div>

                          <button
                            onClick={handleVerifyOtp}
                            disabled={isSubmitting || accessModal.otpValue.length < 6 || accessModal.timeLeft === 0}
                            className="cursor-pointer w-full py-3 bg-blue-600 text-white font-semibold text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-3"
                          >
                            {isSubmitting
                              ? <><i className="ri-loader-4-line animate-spin"></i> Verifying...</>
                              : <><i className="ri-shield-check-line"></i> Verify & Link Brand</>
                            }
                          </button>

                          <p className="text-center text-xs text-gray-500">
                            Did not receive the code?{' '}
                            {accessModal.resendCooldown > 0 ? (
                              <span className="text-gray-400">Resend in {accessModal.resendCooldown}s</span>
                            ) : (
                              <button
                                onClick={handleResendOtp}
                                className="cursor-pointer text-blue-600 font-medium hover:underline"
                              >
                                Resend Code
                              </button>
                            )}
                          </p>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )
        }
      </div >
    );
  }

  return null;
}
